import { Form, Input } from 'antd';
import React, { useEffect, useState } from 'react';
import { Button } from 'shared/components/button';
import { CustomLabel } from 'shared/components/customLabel';
import { ILoginForm } from '../types/loginFormTypes';
import { RoutePath } from 'shared/config/routerConfig/routerConfig';
import './login-form.scss';
import { CustomLink } from 'shared/components/customLink';
import { useLoginMutation } from 'features/auth/api';
import { Loading } from 'shared/components/loading';
import { useNavigate } from 'react-router-dom';
import {
  removeLocalStorage,
  setLocalStorage,
} from 'shared/libs/storageService/localStorage';
import { ACCESS_TOKEN, REFRESH_TOKEN, USER_EMAIL } from 'shared/constants';
import { setCookie } from 'shared/libs/storageService/cookie';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export const LoginForm = () => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const navigate = useNavigate();
  const [fetchLogin, { data, isError, isLoading, error }] = useLoginMutation();
  const [form] = Form.useForm<ILoginForm>();
  const [errorForm, setErrorForm] = useState(false);

  const handleSubmit = async (value: ILoginForm) => {
    setErrorForm(false);
    try {
      await fetchLogin({
        password: value.password,
        username: value.email,
      }).unwrap();
    } catch (e) {
      console.error(e, 'fetch Login');
    }
  };

  useEffect(() => {
    if (data?.access && data.refresh) {
      data?.access && setCookie(ACCESS_TOKEN, data.access, { secure: true });
      data?.refresh && setCookie(REFRESH_TOKEN, data.refresh, { secure: true });
      removeLocalStorage(USER_EMAIL);
      navigate(RoutePath['/invoices/saved']);
    }
  }, [data]);

  useEffect(() => {
    const fetchBaseQueryError = error as FetchBaseQueryError;

    if (fetchBaseQueryError?.status === 409) {
      setLocalStorage(USER_EMAIL, form.getFieldValue('email'));
      navigate(RoutePath['/register/verify']);
    } else if (error) {
      setErrorForm(true);
      form.setFields([
        {
          name: 'email',
          errors: ['Email or Password is incorrect.'],
        },
      ]);
    }
  }, [error]);

  const handleFormValuesChange = () => {
    const values = form.getFieldsValue();
    const allFieldsFilled = Object.values(values).every(
      (value) => value && value.length >= 8
    );
    setIsButtonDisabled(!allFieldsFilled);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      style={{ maxWidth: '400px', width: '100%' }}
      initialValues={{}}
      onFinish={handleSubmit}
      onValuesChange={handleFormValuesChange}
    >
      <Form.Item
        label={<CustomLabel text="Email" />}
        name="email"
        rules={[
          { required: true, message: 'Please input your email!' },
          { type: 'email', message: 'Not an email' },
        ]}
        validateTrigger="onSubmit"
      >
        <Input type="text" className="input" size="large" />
      </Form.Item>

      <Form.Item
        label={<CustomLabel text="Password" />}
        name="password"
        rules={[
          { required: true, message: 'Please input your password!' },
          { min: 8, message: 'Password must be at least 8 characters long' },
        ]}
        validateTrigger="onSubmit"
      >
        <Input.Password
          className={`${errorForm ? 'error-field' : ''}`}
          size="large"
        />
      </Form.Item>

      <div className="buttons-wrapper mt-16">
        <Button
          className="flex-row mb-24"
          type="submit"
          disabled={isLoading || isButtonDisabled}
        >
          Login
          {isLoading && <Loading className="ml-12 spin-white" size="small" />}
        </Button>

        <CustomLink to={RoutePath['/register']}>Create Account</CustomLink>
        <CustomLink to={RoutePath['/reset-password']}>
          Reset Password
        </CustomLink>
      </div>
    </Form>
  );
};

export default LoginForm;
